import LayoutHR from "layouts/layout-hr";
import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { getRequest } from "services/api";

const getDepreciationRate = (asset) => Number(asset?.depreciation_rate || asset?.depreciation_percentage || 0);

const getAssetPrice = (asset) => Number(asset?.price || asset?.purchase_price || asset?.acquisition_value || 0);

const getEstimatedDepreciation = (asset) => getAssetPrice(asset) * (getDepreciationRate(asset) / 100);

const AssetReportPage = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssetReport = async () => {
      try {
        setLoading(true);
        const response = await getRequest("/ahr/report/asset");
        if (response.success) {
          setReportData(response.data);
        }
      } catch (error) {
        console.error("Error fetching asset report:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssetReport();
  }, []);

  const statusChartOptions = {
    chart: {
      type: "donut",
      height: 250,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    labels: reportData?.assets_by_status?.map(item => item.status.charAt(0).toUpperCase() + item.status.slice(1)) || [],
    colors: ["#206bc4", "#d63939", "#2fb344", "#f59e0b", "#667eea"],
    legend: {
      position: "bottom",
      fontFamily: "Inter, sans-serif",
      fontSize: "12px",
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val + "%";
      },
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "11px",
      },
    },
    tooltip: {
      style: {
        fontFamily: "Inter, sans-serif",
      },
      y: {
        formatter: function (value) {
          return value + " assets";
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const statusChartSeries = reportData?.assets_by_status?.map(item => item.count) || [];
  const depreciationSummary = (reportData?.recent_assets || []).reduce((summary, asset) => {
    summary.totalRate += getDepreciationRate(asset);
    summary.totalDepreciation += getEstimatedDepreciation(asset);
    summary.count += 1;
    return summary;
  }, { totalRate: 0, totalDepreciation: 0, count: 0 });
  const averageDepreciationRate = depreciationSummary.count ? depreciationSummary.totalRate / depreciationSummary.count : 0;

  if (loading) {
    return (
      <LayoutHR>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Asset Report</h3>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHR>
    );
  }

  return (
    <LayoutHR>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row ">
          <div className="col-12">
            <div className="page-header">
              <h1 className="page-title">Asset Report</h1>
              <div className="page-subtitle">Comprehensive overview of company assets</div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row ">
          <div className="col-lg-4 ">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-4 font-weight-bold text-primary mb-2">
                  {reportData?.summary?.total_assets || 0}
                </div>
                <div className="text-muted">Total Assets</div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 mb-4">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-4 font-weight-bold text-success mb-2">
                  Rp {new Intl.NumberFormat("id-ID").format(reportData?.summary?.total_value || 0)}
                </div>
                <div className="text-muted">Total Value</div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 mb-4">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-4 font-weight-bold text-warning mb-2">
                  {averageDepreciationRate.toFixed(2)}%
                </div>
                <div className="text-muted">Average Depreciation Rate</div>
                <div className="text-muted small mt-1">
                  Est. yearly: Rp {new Intl.NumberFormat("id-ID").format(depreciationSummary.totalDepreciation)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Tables Row */}
        <div className="row">
          {/* Status Distribution */}
          <div className="col-lg-4 mb-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Assets by Status</h3>
              </div>
              <div className="card-body">
                {statusChartSeries.length > 0 ? (
                  <Chart
                    options={statusChartOptions}
                    series={statusChartSeries}
                    type="donut"
                    height={250}
                  />
                ) : (
                  <div className="text-center text-muted">
                    No status data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Assets by Group */}
          <div className="col-lg-8 mb-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Assets by Group</h3>
              </div>
              <div className="card-body">
                {reportData?.assets_by_group?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Group Name</th>
                          <th className="text-center">Count</th>
                          <th className="text-end">Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.assets_by_group.map((group, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div>
                                  <div className="font-weight-medium">{group.asset_group.name}</div>
                                  {group.asset_group.description && (
                                    <div className="text-muted small">{group.asset_group.description}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-primary text-white">{group.count}</span>
                            </td>
                            <td className="text-end font-weight-medium">
                              Rp {new Intl.NumberFormat("id-ID").format(group.total_value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    No group data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Assets */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Recent Assets</h3>
              </div>
              <div className="card-body">
                {reportData?.recent_assets?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Asset Name</th>
                          <th>Group</th>
                          <th>Status</th>
                          <th className="text-end">Price</th>
                          <th className="text-center">Depreciation</th>
                          <th className="text-center">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.recent_assets.map((asset, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  {asset.image ? (
                                    <img
                                      src={asset.image}
                                      alt={asset.name}
                                      className="rounded"
                                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      className="bg-light rounded d-flex align-items-center justify-content-center"
                                      style={{ width: "40px", height: "40px" }}
                                    >
                                      <i className="ti ti-package text-muted"></i>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="font-weight-medium">{asset.name}</div>
                                  {asset.description && (
                                    <div className="text-muted small">{asset.description}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary text-white">{asset.asset_group.name}</span>
                            </td>
                            <td>
                              <span className={`badge text-white ${asset.status === 'available' ? 'bg-success' : 'bg-warning'}`}>
                                {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
                              </span>
                            </td>
                            <td className="text-end font-weight-medium">
                              Rp {new Intl.NumberFormat("id-ID").format(getAssetPrice(asset))}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-warning-lt text-warning">{getDepreciationRate(asset)}%</span>
                              <div className="text-muted small">
                                Rp {new Intl.NumberFormat("id-ID").format(getEstimatedDepreciation(asset))}/tahun
                              </div>
                            </td>
                            <td className="text-center text-muted">
                              {new Date(asset.created_at).toLocaleDateString('id-ID', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    No recent assets available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHR>
  );
};

export default AssetReportPage;
